import React, { useRef, useEffect, useState } from 'react';

/**
 * CinematicBackground - Beautiful, lightweight cinematic backgrounds
 * 
 * Uses CSS animations instead of canvas for performance
 * Creates a mesmerizing, theatrical atmosphere
 */

// CSS-based Van Gogh-inspired animated gradient
export const VanGoghCanvas = ({ 
  palette = 'starryNight',
  speed = 0.3,
  intensity = 1,
  className = ''
}) => {
  const palettes = {
    starryNight: ['#192845', '#2D5090', '#FFD750', '#FFF5B4', '#283C5A', '#6494C8'],
    sunflowers: ['#FFC832', '#C87820', '#FFE664', '#964C14', '#503C28', '#FFB43C'],
    cafeNight: ['#1E1432', '#FFDC64', '#3C3250', '#C89632', '#645078', '#FFC850'],
    irisField: ['#503C8C', '#7864B4', '#3C643C', '#649650', '#C8B4DC', '#FFE6C8'],
    almond: ['#B4DCF0', '#FFF0F5', '#503C28', '#DCB4BE', '#8CB4C8', '#FFFFFF'],
  };
  
  const colors = palettes[palette] || palettes.starryNight;
  
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Base gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[4]} 50%, ${colors[0]} 100%)`,
        }}
      />
      
      {/* Animated swirling layers */}
      {colors.map((color, i) => (
        <div
          key={i}
          className="absolute rounded-full blur-3xl mix-blend-soft-light"
          style={{
            width: `${200 + i * 100}px`,
            height: `${200 + i * 100}px`,
            background: `radial-gradient(circle, ${color}80 0%, transparent 70%)`,
            left: `${10 + i * 15}%`,
            top: `${10 + i * 10}%`,
            animation: `float-${i} ${20 + i * 5}s ease-in-out infinite`,
            opacity: 0.6 * intensity,
          }}
        />
      ))}
      
      {/* Starry particles */}
      {[...Array(20)].map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute rounded-full"
          style={{
            width: `${2 + Math.random() * 4}px`,
            height: `${2 + Math.random() * 4}px`,
            background: colors[2],
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `twinkle ${3 + Math.random() * 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
            opacity: 0.7,
          }}
        />
      ))}
      
      <style>{`
        @keyframes float-0 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(50px, 30px) rotate(5deg); }
          50% { transform: translate(20px, 60px) rotate(-5deg); }
          75% { transform: translate(-30px, 40px) rotate(3deg); }
        }
        @keyframes float-1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(-40px, 50px) rotate(-8deg); }
          66% { transform: translate(30px, 20px) rotate(5deg); }
        }
        @keyframes float-2 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(60px, -30px); }
        }
        @keyframes float-3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-50px, 50px) scale(1.1); }
        }
        @keyframes float-4 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(30px, -40px) rotate(10deg); }
          75% { transform: translate(-40px, 30px) rotate(-10deg); }
        }
        @keyframes float-5 {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(40px, 40px); }
          66% { transform: translate(-20px, 60px); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
      `}</style>
    </div>
  );
};

// CSS-based Alpha-Theta Spiral Effect
export const AlphaThetaSpiral = ({ 
  speed = 0.2,
  colorShift = true,
  className = ''
}) => {
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Dark base */}
      <div className="absolute inset-0 bg-[#080A0E]" />
      
      {/* Rotating spiral rings */}
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
          style={{
            width: `${100 + i * 80}px`,
            height: `${60 + i * 50}px`,
            borderColor: `hsla(${40 + i * 30}, 60%, 60%, 0.15)`,
            animation: `spin-slow ${30 + i * 10}s linear infinite`,
            animationDirection: i % 2 === 0 ? 'normal' : 'reverse',
          }}
        />
      ))}
      
      {/* Central glow */}
      <div 
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(251, 191, 36, 0.2) 0%, transparent 70%)',
          animation: 'pulse-glow 4s ease-in-out infinite',
        }}
      />
      
      <style>{`
        @keyframes spin-slow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

// Combined cinematic background with video support
export const CinematicBackground = ({
  mode = 'vangogh', // 'vangogh', 'spiral', 'video', 'random'
  videoUrl = null,
  palette = 'starryNight',
  speed = 0.2,
  transitionInterval = 30000,
  className = ''
}) => {
  const [currentMode, setCurrentMode] = useState(mode === 'random' ? 'vangogh' : mode);
  const videoRef = useRef(null);
  
  // Random mode switching
  useEffect(() => {
    if (mode !== 'random') {
      setCurrentMode(mode);
      return;
    }
    
    const modes = videoUrl ? ['vangogh', 'spiral', 'video'] : ['vangogh', 'spiral'];
    
    const switchMode = () => {
      let newMode;
      do {
        newMode = modes[Math.floor(Math.random() * modes.length)];
      } while (newMode === currentMode && modes.length > 1);
      setCurrentMode(newMode);
    };
    
    const interval = setInterval(switchMode, transitionInterval);
    return () => clearInterval(interval);
  }, [mode, videoUrl, currentMode, transitionInterval]);
  
  // Video playback
  useEffect(() => {
    if (currentMode === 'video' && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [currentMode]);
  
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Base dark layer */}
      <div className="absolute inset-0 bg-[#080A0E]" />
      
      {/* Effect layers */}
      {currentMode === 'vangogh' && (
        <VanGoghCanvas palette={palette} speed={speed} intensity={1} />
      )}
      
      {currentMode === 'spiral' && (
        <AlphaThetaSpiral speed={speed} colorShift={true} />
      )}
      
      {currentMode === 'video' && videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          autoPlay
          loop
          muted
          playsInline
        />
      )}
      
      {/* Vignette overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)'
        }}
      />
    </div>
  );
};

export default CinematicBackground;
